import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function PmsPolicyConfig({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [form, setForm] = useState({
    policy_type: 'STANDARD',
    default_checkin_time: '12:00',
    default_checkout_time: '11:00',
    late_checkout_grace_minutes: 30,
    late_checkout_hourly_charge: 0,
    room_color_vacant: '#86efac',
    room_color_reserved: '#f59e0b',
    room_color_occupied: '#60a5fa',
    room_color_blocked: '#f87171',
    room_color_dirty_vacant: '#facc15'
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Invoice Amendment Policy — stored on pms_property_config but managed via
  // a dedicated endpoint so the GET /property-config payload stays unchanged.
  // SRS: docs/SRS_INVOICE_AMENDMENT.md §10.4
  const [amendPolicy, setAmendPolicy] = useState('SAME_BUSINESS_DATE');
  const [amendWindowDays, setAmendWindowDays] = useState(7);
  const [amendBusy, setAmendBusy] = useState(false);
  const [amendMsg, setAmendMsg] = useState('');
  const [amendErr, setAmendErr] = useState('');

  // Night Audit auto-run timing — same "dedicated endpoint" pattern as above.
  // SRS: docs/SRS_AMBERPARAMOUNT_REQ02-06.md REQ-03
  const [nightAuditEnabled, setNightAuditEnabled] = useState(true);
  const [nightAuditTime, setNightAuditTime] = useState('00:05');
  const [nightAuditBusy, setNightAuditBusy] = useState(false);
  const [nightAuditMsg, setNightAuditMsg] = useState('');
  const [nightAuditErr, setNightAuditErr] = useState('');

  // OTA Booking Auto Room Assignment — same "dedicated endpoint" pattern.
  // SRS: docs/SRS_AMBERPARAMOUNT_REQ08-13.md REQ-08
  const [otaAutoAssignEnabled, setOtaAutoAssignEnabled] = useState(false);
  const [otaAutoAssignBusy, setOtaAutoAssignBusy] = useState(false);
  const [otaAutoAssignMsg, setOtaAutoAssignMsg] = useState('');
  const [otaAutoAssignErr, setOtaAutoAssignErr] = useState('');

  // Checkout Auto Round Off — same "dedicated endpoint" pattern.
  const [roundOffEnabled, setRoundOffEnabled] = useState(false);
  const [roundOffBusy, setRoundOffBusy] = useState(false);
  const [roundOffMsg, setRoundOffMsg] = useState('');
  const [roundOffErr, setRoundOffErr] = useState('');

  // Front Office Dashboard Alert Banners — same "dedicated endpoint" pattern.
  const [showLateCheckoutAlert, setShowLateCheckoutAlert] = useState(true);
  const [showNightAuditAlert, setShowNightAuditAlert] = useState(true);
  const [alertVisBusy, setAlertVisBusy] = useState(false);
  const [alertVisMsg, setAlertVisMsg] = useState('');
  const [alertVisErr, setAlertVisErr] = useState('');

  // Room-Charge Posting Timing (REQ-16) — same "dedicated endpoint" pattern.
  // SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md §11
  const [roomChargePostingMode, setRoomChargePostingMode] = useState('NIGHT_AUDIT');
  const [postingModeBusy, setPostingModeBusy] = useState(false);
  const [postingModeMsg, setPostingModeMsg] = useState('');
  const [postingModeErr, setPostingModeErr] = useState('');

  // Invoice Terms & Conditions (REQ-18) — same "dedicated endpoint" pattern.
  // SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md
  const [invoiceTerms, setInvoiceTerms] = useState('');
  const [invoiceTermsBusy, setInvoiceTermsBusy] = useState(false);
  const [invoiceTermsMsg, setInvoiceTermsMsg] = useState('');
  const [invoiceTermsErr, setInvoiceTermsErr] = useState('');

  // Invoice Generation Mode (REQ-23) — same "dedicated endpoint" pattern.
  // SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md §18
  const [invoiceGenerationMode, setInvoiceGenerationMode] = useState('AUTO');
  const [invoiceGenModeBusy, setInvoiceGenModeBusy] = useState(false);
  const [invoiceGenModeMsg, setInvoiceGenModeMsg] = useState('');
  const [invoiceGenModeErr, setInvoiceGenModeErr] = useState('');

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  useEffect(() => {
    if (!selectedProperty?.id) return;
    setMessage(''); setError('');
    fetch('/api/v1/pms/property-config', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) setForm({
          policy_type: data.policy_type || 'STANDARD',
          default_checkin_time: data.default_checkin_time || '12:00',
          default_checkout_time: data.default_checkout_time || '11:00',
          late_checkout_grace_minutes: Number(data.late_checkout_grace_minutes ?? 30),
          late_checkout_hourly_charge: Number(data.late_checkout_hourly_charge ?? 0),
          room_color_vacant: data.room_color_vacant || '#86efac',
          room_color_reserved: data.room_color_reserved || '#f59e0b',
          room_color_occupied: data.room_color_occupied || '#60a5fa',
          room_color_blocked: data.room_color_blocked || '#f87171',
          room_color_dirty_vacant: data.room_color_dirty_vacant || '#facc15'
        });
      }).catch(() => {});

    // Load amendment policy (separate endpoint).
    fetch('/api/v1/pms/property-config/invoice-amend-policy', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setAmendPolicy(data.invoice_amend_policy || 'SAME_BUSINESS_DATE');
          setAmendWindowDays(Number(data.invoice_amend_window_days ?? 7));
        }
      }).catch(() => {});

    // Load night audit timing (separate endpoint).
    fetch('/api/v1/pms/property-config/night-audit-timing', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setNightAuditEnabled(data.night_audit_auto_enabled !== false);
          setNightAuditTime(data.night_audit_auto_time || '00:05');
        }
      }).catch(() => {});

    // Load OTA auto room assignment toggle (separate endpoint).
    fetch('/api/v1/pms/property-config/ota-auto-room-assignment', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setOtaAutoAssignEnabled(data.ota_auto_room_assignment_enabled === true);
        }
      }).catch(() => {});

    // Load checkout auto round off toggle (separate endpoint).
    fetch('/api/v1/pms/property-config/checkout-auto-round-off', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setRoundOffEnabled(data.checkout_auto_round_off_enabled === true);
        }
      }).catch(() => {});

    // Load room-charge posting mode (separate endpoint).
    fetch('/api/v1/pms/property-config/room-charge-posting-mode', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setRoomChargePostingMode(data.room_charge_posting_mode || 'NIGHT_AUDIT');
        }
      }).catch(() => {});

    // Load dashboard alert banner visibility (separate endpoint).
    fetch('/api/v1/pms/property-config/dashboard-alert-visibility', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setShowLateCheckoutAlert(data.show_late_checkout_alert !== false);
          setShowNightAuditAlert(data.show_night_audit_alert !== false);
        }
      }).catch(() => {});

    // Load invoice Terms & Conditions text (separate endpoint).
    fetch('/api/v1/pms/property-config/invoice-terms', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setInvoiceTerms(data.invoice_terms_text || '');
        }
      }).catch(() => {});

    // Load invoice generation mode (separate endpoint).
    fetch('/api/v1/pms/property-config/invoice-generation-mode', { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) {
          setInvoiceGenerationMode(data.invoice_generation_mode || 'AUTO');
        }
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id]);

  const saveNightAuditTiming = async () => {
    if (!selectedProperty?.id) return;
    setNightAuditBusy(true); setNightAuditMsg(''); setNightAuditErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/night-audit-timing', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({
          night_audit_auto_enabled: nightAuditEnabled,
          night_audit_auto_time: nightAuditTime
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save night audit timing');
      setNightAuditMsg('Night audit timing saved.');
    } catch (e) {
      setNightAuditErr(e?.message || 'Save failed');
    } finally { setNightAuditBusy(false); }
  };

  const saveOtaAutoAssign = async () => {
    if (!selectedProperty?.id) return;
    setOtaAutoAssignBusy(true); setOtaAutoAssignMsg(''); setOtaAutoAssignErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/ota-auto-room-assignment', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ ota_auto_room_assignment_enabled: otaAutoAssignEnabled })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save OTA auto room assignment');
      setOtaAutoAssignMsg('OTA auto room assignment setting saved.');
    } catch (e) {
      setOtaAutoAssignErr(e?.message || 'Save failed');
    } finally { setOtaAutoAssignBusy(false); }
  };

  const saveRoundOff = async () => {
    if (!selectedProperty?.id) return;
    setRoundOffBusy(true); setRoundOffMsg(''); setRoundOffErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/checkout-auto-round-off', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ checkout_auto_round_off_enabled: roundOffEnabled })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save checkout auto round off');
      setRoundOffMsg('Checkout auto round off setting saved.');
    } catch (e) {
      setRoundOffErr(e?.message || 'Save failed');
    } finally { setRoundOffBusy(false); }
  };

  const saveAlertVisibility = async () => {
    if (!selectedProperty?.id) return;
    setAlertVisBusy(true); setAlertVisMsg(''); setAlertVisErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/dashboard-alert-visibility', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({
          show_late_checkout_alert: showLateCheckoutAlert,
          show_night_audit_alert: showNightAuditAlert
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save alert banner visibility');
      setAlertVisMsg('Dashboard alert banner visibility saved.');
    } catch (e) {
      setAlertVisErr(e?.message || 'Save failed');
    } finally { setAlertVisBusy(false); }
  };

  const saveRoomChargePostingMode = async () => {
    if (!selectedProperty?.id) return;
    setPostingModeBusy(true); setPostingModeMsg(''); setPostingModeErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/room-charge-posting-mode', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ room_charge_posting_mode: roomChargePostingMode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save room-charge posting timing');
      setPostingModeMsg('Room-charge posting timing saved.');
    } catch (e) {
      setPostingModeErr(e?.message || 'Save failed');
    } finally { setPostingModeBusy(false); }
  };

  const saveInvoiceTerms = async () => {
    if (!selectedProperty?.id) return;
    setInvoiceTermsBusy(true); setInvoiceTermsMsg(''); setInvoiceTermsErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/invoice-terms', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ invoice_terms_text: invoiceTerms })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save invoice terms & conditions');
      setInvoiceTermsMsg('Invoice Terms & Conditions saved.');
    } catch (e) {
      setInvoiceTermsErr(e?.message || 'Save failed');
    } finally { setInvoiceTermsBusy(false); }
  };

  const saveInvoiceGenerationMode = async () => {
    if (!selectedProperty?.id) return;
    setInvoiceGenModeBusy(true); setInvoiceGenModeMsg(''); setInvoiceGenModeErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/invoice-generation-mode', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ invoice_generation_mode: invoiceGenerationMode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save invoice generation mode');
      setInvoiceGenModeMsg('Invoice generation mode saved.');
    } catch (e) {
      setInvoiceGenModeErr(e?.message || 'Save failed');
    } finally { setInvoiceGenModeBusy(false); }
  };

  const saveAmendPolicy = async () => {
    if (!selectedProperty?.id) return;
    setAmendBusy(true); setAmendMsg(''); setAmendErr('');
    try {
      const res = await fetch('/api/v1/pms/property-config/invoice-amend-policy', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({
          invoice_amend_policy: amendPolicy,
          invoice_amend_window_days: Number(amendWindowDays) || 7
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save amendment policy');
      setAmendMsg('Invoice amendment policy saved.');
    } catch (e) {
      setAmendErr(e?.message || 'Save failed');
    } finally { setAmendBusy(false); }
  };

  const save = async () => {
    if (!selectedProperty?.id) return;
    setBusy(true); setMessage(''); setError('');
    try {
      const res = await fetch('/api/v1/pms/property-config', {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({
          policy_type: form.policy_type,
          default_checkin_time: form.default_checkin_time,
          default_checkout_time: form.policy_type === '24_HOUR' ? form.default_checkin_time : form.default_checkout_time,
          late_checkout_grace_minutes: Number(form.late_checkout_grace_minutes),
          late_checkout_hourly_charge: Number(form.late_checkout_hourly_charge),
          room_color_vacant: form.room_color_vacant,
          room_color_reserved: form.room_color_reserved,
          room_color_occupied: form.room_color_occupied,
          room_color_blocked: form.room_color_blocked,
          room_color_dirty_vacant: form.room_color_dirty_vacant
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      setMessage('Policy saved successfully.');
    } catch (err) {
      setError(err?.message || 'Failed to save policy');
    } finally {
      setBusy(false);
    }
  };

  const is24h = form.policy_type === '24_HOUR';

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>PMS Policy</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>PMS Policy Configuration</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Configure property check-in/out times, late checkout rules and room color codes</p>
      {!selectedProperty?.id && <div className="info-banner">Select a property first from Hotel Config &gt; Select Property.</div>}

      <div className="card">
      <div className="card-body" style={{ display: 'grid', gap: '1.2rem' }}>

        {/* Policy Type */}
        <div>
          <div className="section-head">Policy Type</div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {[['STANDARD', 'Standard (Fixed Check-In / Check-Out Time)'], ['24_HOUR', '24-Hour (Check-Out = Check-In + 24 hrs)']].map(([val, label]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: form.policy_type === val ? 700 : 400 }}>
                <input type="radio" name="policy_type" value={val} checked={form.policy_type === val} onChange={() => setForm((f) => ({ ...f, policy_type: val }))} />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Time Policy */}
        <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.9rem' }}>
          {is24h ? (
            <div>
              <div className="section-head" style={{color:'#0f766e'}}>24-Hour Policy</div>
              <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.88rem', color: '#4a5568' }}>
                Guest check-in can happen at any time on the arrival date. Check-out is exactly <b>24 hours</b> after check-in. Late check-out is calculated beyond this 24-hour window.
              </p>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, maxWidth: 200 }}>
                Standard Check-In Time (reference)
                <input type="time" value={form.default_checkin_time} onChange={(e) => setForm((f) => ({ ...f, default_checkin_time: e.target.value }))} style={{ padding: '8px' }} />
              </label>
            </div>
          ) : (
            <div>
              <div className="section-head" style={{color:'#1d4ed8'}}>Standard Policy</div>
              <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.88rem', color: '#4a5568' }}>
                Standard check-in is <b>{form.default_checkin_time}</b> and check-out <b>{form.default_checkout_time}</b>. Front office can still check a guest in early, at any time on the arrival date.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600 }}>
                  Check-In Time
                  <input type="time" value={form.default_checkin_time} onChange={(e) => setForm((f) => ({ ...f, default_checkin_time: e.target.value }))} style={{ padding: '8px' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600 }}>
                  Check-Out Time
                  <input type="time" value={form.default_checkout_time} onChange={(e) => setForm((f) => ({ ...f, default_checkout_time: e.target.value }))} style={{ padding: '8px' }} />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Late Checkout */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '0.8rem' }}>
          <div className="section-head">Late Check-Out Rules</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600 }}>
              Grace Period (minutes)
              <input type="number" min="0" step="1" placeholder="30" value={form.late_checkout_grace_minutes} onChange={(e) => setForm((f) => ({ ...f, late_checkout_grace_minutes: e.target.value }))} style={{ padding: '8px' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600 }}>
              Charge per Hour (₹)
              <input type="number" min="0" step="0.01" placeholder="0.00" value={form.late_checkout_hourly_charge} onChange={(e) => setForm((f) => ({ ...f, late_checkout_hourly_charge: e.target.value }))} style={{ padding: '8px' }} />
            </label>
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: '#6b7280' }}>
            If checkout exceeds the expected time by more than <b>{form.late_checkout_grace_minutes} min</b>, a charge of <b>₹{Number(form.late_checkout_hourly_charge || 0).toFixed(2)}/hr</b> is applied.
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '0.8rem' }}>
          <div className="section-head">Room Status Color Code</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontWeight: 600 }}>
              Vacant
              <input type="color" value={form.room_color_vacant} onChange={(e) => setForm((f) => ({ ...f, room_color_vacant: e.target.value }))} style={{ width: '56px', height: '32px', padding: 0, border: '1px solid #cbd5e0', borderRadius: '4px' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontWeight: 600 }}>
              Reserved
              <input type="color" value={form.room_color_reserved} onChange={(e) => setForm((f) => ({ ...f, room_color_reserved: e.target.value }))} style={{ width: '56px', height: '32px', padding: 0, border: '1px solid #cbd5e0', borderRadius: '4px' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontWeight: 600 }}>
              Occupied
              <input type="color" value={form.room_color_occupied} onChange={(e) => setForm((f) => ({ ...f, room_color_occupied: e.target.value }))} style={{ width: '56px', height: '32px', padding: 0, border: '1px solid #cbd5e0', borderRadius: '4px' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontWeight: 600 }}>
              Blocked / OOO
              <input type="color" value={form.room_color_blocked} onChange={(e) => setForm((f) => ({ ...f, room_color_blocked: e.target.value }))} style={{ width: '56px', height: '32px', padding: 0, border: '1px solid #cbd5e0', borderRadius: '4px' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontWeight: 600 }}>
              Dirty Vacant
              <input type="color" value={form.room_color_dirty_vacant} onChange={(e) => setForm((f) => ({ ...f, room_color_dirty_vacant: e.target.value }))} style={{ width: '56px', height: '32px', padding: 0, border: '1px solid #cbd5e0', borderRadius: '4px' }} />
            </label>
          </div>
        </div>

        {error ? <div className="error-banner">{error}</div> : null}
        {message ? <div className="success-banner">{message}</div> : null}

        <div style={{ textAlign: 'right' }}>
          <button onClick={save} disabled={busy || !selectedProperty?.id} className="btn-primary" style={{ padding: '0.55rem 1.6rem', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Saving...' : 'Save Policy'}
          </button>
        </div>

        {/* Invoice Amendment Policy — controls how long after issue an invoice */}
        {/* can be amended. SRS_INVOICE_AMENDMENT.md §10.4.                     */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Invoice Amendment Policy</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Controls how long after issue an invoice can be amended (narrative edit or Cancel &amp; Reissue).
            Anything affecting amounts/lines/tax always routes through Cancel &amp; Reissue (issues a Credit Note).
            Default is <b>Same business date</b> (conservative).
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', alignItems: 'end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600 }}>
              Window policy
              <select value={amendPolicy} onChange={(e) => setAmendPolicy(e.target.value)} style={{ padding: '8px' }}>
                <option value="DISABLED">Disabled (no amendment allowed)</option>
                <option value="SAME_BUSINESS_DATE">Same business date as issue (recommended)</option>
                <option value="DAYS_N">Within N days of issue</option>
                <option value="SAME_MONTH">Same calendar month as issue</option>
                <option value="NO_LIMIT">No limit (always amendable — audit risk)</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, opacity: amendPolicy === 'DAYS_N' ? 1 : 0.5 }}>
              Days (used when policy = "Within N days")
              <input type="number" min="1" max="365" step="1" value={amendWindowDays}
                disabled={amendPolicy !== 'DAYS_N'}
                onChange={(e) => setAmendWindowDays(e.target.value)}
                style={{ padding: '8px' }} />
            </label>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Only users with the <b>Invoice Amendment</b> permission (auto-granted to admin / hotel admin) can use the feature.
          </div>
          {amendErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{amendErr}</div> : null}
          {amendMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{amendMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveAmendPolicy} disabled={amendBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: amendBusy ? 0.7 : 1 }}>
              {amendBusy ? 'Saving...' : 'Save Amendment Policy'}
            </button>
          </div>
        </div>

        {/* Night Audit Timing — when the automatic close/rollover job runs. */}
        {/* SRS: docs/SRS_AMBERPARAMOUNT_REQ02-06.md REQ-03.                   */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Night Audit Timing</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Controls when the automatic Night Audit runs and closes the business date. The audit for a date can only
            run once that date has ended — a time of <b>11:00</b> means the audit for yesterday's business date
            runs at 11:00 AM today, not at midnight. Default is <b>00:05</b>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={nightAuditEnabled} onChange={(e) => setNightAuditEnabled(e.target.checked)} />
              Auto-run enabled
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, opacity: nightAuditEnabled ? 1 : 0.5 }}>
              Auto-run time
              <input type="time" value={nightAuditTime} disabled={!nightAuditEnabled}
                onChange={(e) => setNightAuditTime(e.target.value)} style={{ padding: '8px' }} />
            </label>
          </div>
          {nightAuditErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{nightAuditErr}</div> : null}
          {nightAuditMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{nightAuditMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveNightAuditTiming} disabled={nightAuditBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: nightAuditBusy ? 0.7 : 1 }}>
              {nightAuditBusy ? 'Saving...' : 'Save Night Audit Timing'}
            </button>
          </div>
        </div>

        {/* OTA Booking Auto Room Assignment. */}
        {/* SRS: docs/SRS_AMBERPARAMOUNT_REQ08-13.md REQ-08.                    */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">OTA Booking Auto Room Assignment</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            When enabled, an OTA-sourced reservation with no room picked yet is automatically assigned an available
            room of the booked type at check-in. Staff can still pick a room manually (that always takes
            precedence), and can change the assigned room afterwards via Room Change. Default is <b>off</b>.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={otaAutoAssignEnabled} onChange={(e) => setOtaAutoAssignEnabled(e.target.checked)} />
            Auto-assign a room for OTA bookings at check-in
          </label>
          {otaAutoAssignErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{otaAutoAssignErr}</div> : null}
          {otaAutoAssignMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{otaAutoAssignMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveOtaAutoAssign} disabled={otaAutoAssignBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: otaAutoAssignBusy ? 0.7 : 1 }}>
              {otaAutoAssignBusy ? 'Saving...' : 'Save OTA Auto Room Assignment'}
            </button>
          </div>
        </div>

        {/* Checkout Auto Round Off. */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Checkout Auto Round Off</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            When enabled, the amount to collect at checkout is rounded to the nearest whole rupee. The paisa
            difference is posted to the guest folio as a <b>Round Off</b> line so the balance settles to zero once
            the rounded amount is collected. Applies to direct payment, city ledger, and corporate/travel-agent
            settlement alike. Default is <b>off</b>.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={roundOffEnabled} onChange={(e) => setRoundOffEnabled(e.target.checked)} />
            Round off amount to collect at checkout
          </label>
          {roundOffErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{roundOffErr}</div> : null}
          {roundOffMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{roundOffMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveRoundOff} disabled={roundOffBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: roundOffBusy ? 0.7 : 1 }}>
              {roundOffBusy ? 'Saving...' : 'Save Checkout Auto Round Off'}
            </button>
          </div>
        </div>

        {/* Front Office Dashboard Alert Banners. */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Front Office Dashboard Alert Banners</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Controls whether the Late Checkout Alert and Night Audit Exceptions banners appear on the Front Office
            dashboard for everyone at this property. Both default to <b>on</b>.
          </p>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={showLateCheckoutAlert} onChange={(e) => setShowLateCheckoutAlert(e.target.checked)} />
              ⚠ Show Late Checkout Alert
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={showNightAuditAlert} onChange={(e) => setShowNightAuditAlert(e.target.checked)} />
              ⏰ Show Night Audit Exceptions
            </label>
          </div>
          {alertVisErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{alertVisErr}</div> : null}
          {alertVisMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{alertVisMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveAlertVisibility} disabled={alertVisBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: alertVisBusy ? 0.7 : 1 }}>
              {alertVisBusy ? 'Saving...' : 'Save Alert Banner Visibility'}
            </button>
          </div>
        </div>

        {/* Room-Charge Posting Timing (REQ-16). */}
        {/* SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md §11.                       */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Room-Charge Posting Timing</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Controls when a guest's room charges appear on the folio. <b>Nightly via Night Audit</b> (default) posts
            one night at check-in and each remaining night as it occurs — today's behavior. <b>Full stay at
            check-in</b> posts every night of the stay to the folio immediately at check-in instead of waiting for
            Night Audit. If a guest checks out early after this posted the full stay, front office must remove the
            unconsumed night(s) manually via the usual folio adjustment — this is not reversed automatically.
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, maxWidth: '360px' }}>
            Posting mode
            <select value={roomChargePostingMode} onChange={(e) => setRoomChargePostingMode(e.target.value)} style={{ padding: '8px' }}>
              <option value="NIGHT_AUDIT">Nightly via Night Audit (default)</option>
              <option value="FULL_STAY_AT_CHECKIN">Full stay at check-in</option>
            </select>
          </label>
          {postingModeErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{postingModeErr}</div> : null}
          {postingModeMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{postingModeMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveRoomChargePostingMode} disabled={postingModeBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: postingModeBusy ? 0.7 : 1 }}>
              {postingModeBusy ? 'Saving...' : 'Save Room-Charge Posting Timing'}
            </button>
          </div>
        </div>

        {/* Invoice Terms & Conditions (REQ-18). */}
        {/* SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md.                            */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Invoice Terms & Conditions</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            The text printed at the bottom of every invoice (one line per line of text below). Defaults to the
            standard 3-line text every invoice already showed; edit it to match this property's own terms.
          </p>
          <textarea
            value={invoiceTerms}
            onChange={(e) => setInvoiceTerms(e.target.value)}
            rows={5}
            style={{ width: '100%', maxWidth: '640px', padding: '8px', fontFamily: 'inherit', fontSize: '0.85rem' }}
          />
          {invoiceTermsErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{invoiceTermsErr}</div> : null}
          {invoiceTermsMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{invoiceTermsMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveInvoiceTerms} disabled={invoiceTermsBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: invoiceTermsBusy ? 0.7 : 1 }}>
              {invoiceTermsBusy ? 'Saving...' : 'Save Invoice Terms & Conditions'}
            </button>
          </div>
        </div>

        {/* Invoice Generation Mode (REQ-23). */}
        {/* SRS: docs/SRS_AMBERPARAMOUNT_REQ08-15.md §18.                        */}
        <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '1.2rem', marginTop: '0.6rem' }}>
          <div className="section-head">Invoice Generation</div>
          <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            Controls when a guest's invoice is created. <b>Automatic</b> (default) creates the invoice the moment a
            guest checks out — today's behavior. <b>Manual</b> creates no invoice at checkout; front office clicks
            <b> Generate Invoice</b> (Invoice Register → Pending Generation) whenever they want it created, using the
            settlement details already recorded at checkout.
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, maxWidth: '360px' }}>
            Generation mode
            <select value={invoiceGenerationMode} onChange={(e) => setInvoiceGenerationMode(e.target.value)} style={{ padding: '8px' }}>
              <option value="AUTO">Automatic at checkout (default)</option>
              <option value="MANUAL">Manual — generate on demand</option>
            </select>
          </label>
          {invoiceGenModeErr ? <div className="error-banner" style={{ marginTop: '0.6rem' }}>{invoiceGenModeErr}</div> : null}
          {invoiceGenModeMsg ? <div className="success-banner" style={{ marginTop: '0.6rem' }}>{invoiceGenModeMsg}</div> : null}
          <div style={{ textAlign: 'right', marginTop: '0.7rem' }}>
            <button onClick={saveInvoiceGenerationMode} disabled={invoiceGenModeBusy || !selectedProperty?.id}
              className="btn-primary"
              style={{ padding: '0.5rem 1.4rem', opacity: invoiceGenModeBusy ? 0.7 : 1 }}>
              {invoiceGenModeBusy ? 'Saving...' : 'Save Invoice Generation Mode'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default PmsPolicyConfig;
